/**
 * Implementation primitives for the Map **object-track** polyline layer
 * (ADR-041 D-4; REQ-UI-167 / -168 / -169 / -173 / -181) — source + line
 * layer, GeoJSON conversion, the age fade and the selected-track emphasis.
 * Split from :file:`object-track-layer.tsx` per
 * ``react-refresh/only-export-components``; mirrors
 * :file:`track-tail-layer-impl.ts` (the per-Producer tails).
 *
 * # What is drawn
 *
 * One ``LineString`` per **presented** object track (two or more members)
 * through its **positioned** members in time order (REQ-UI-167). A track with
 * fewer than two positioned members draws no line — its head glyph alone
 * stands for it (never a fabricated point). The line is antimeridian-unwrapped
 * (:func:`map/antimeridian.toUnwrappedCoordinates`) so a crossing stays
 * continuous (REQ-UI-012 precedent).
 *
 * The line is painted in the detection glyph colour (the Profile's
 * ``map.detection.single_color``, default the ``--detection-glyph`` token) so
 * the head glyph and its line read as one object (REQ-UI-046 legibility:
 * distinct from every ``--status-*`` hue and from the alert glyph). Width is
 * :data:`OBJECT_TRACK_LINE_WIDTH_PX`; the selected track (Phase D wires the
 * ``track`` selection kind) paints wider via a data-driven ``line-width`` on
 * the ``selected`` feature property, **in the same colour** — the selection
 * is carried by width and by the patterned overlay (REQ-UI-200), never by a
 * hue. From 2026-10-09 to 2026-10-19 the selected line was repainted in the
 * brand action accent; withdrawn (architect direction 2026-10-19) because the
 * detection colour is operator-configurable, so an operator could set it to
 * the accent and lose the distinction — a hue-based emphasis cannot be made
 * safe against a hue the operator controls.
 *
 * # Age fade (REQ-UI-168)
 *
 * ``line-opacity`` is data-driven on the feature's ``age_fraction`` — the
 * head's age divided by the live window, in ``[0, 1]`` — stepping from
 * :data:`OBJECT_TRACK_OPACITY_FRESH` down to :data:`OBJECT_TRACK_OPACITY_STALE`,
 * so a track that has stopped reporting visibly fades before the store drops
 * it at the window's end (REQ-UI-181). The fraction is computed at conversion
 * time from the caller's ``now``; the layer re-converts on every store change
 * and on a slow tick, so a silent track keeps fading.
 *
 * # Head-only glyphs (REQ-UI-169)
 *
 * Not here — the detection glyph layer hides a track's non-head members via
 * :func:`detection-layer-impl.nonHeadReportIds`, so the line + one head glyph
 * is the whole rendering of a track (ADR-041 D-4).
 *
 * Cross-references:
 *   - REQ-UI-167 / -168 / -169 / -173 / -181; REQ-UI-046; REQ-UI-012.
 *   - ADR-041 D-4 (map rendering) / D-7 (the live window).
 *   - map/track-tail-layer-impl.ts (the sibling this mirrors).
 */
import type {
  ExpressionSpecification,
  FilterSpecification,
  GeoJSONSource,
  Map as MapLibreMap,
} from "maplibre-gl";
import { toUnwrappedCoordinates } from "@/map/antimeridian";
import {
  AGE_FADE_STEP_FRACTIONS,
  AGE_FADE_TICK_CEILING_MS,
  ageFraction,
  ageStepIndex,
  composeAgeFractionExpression,
} from "@/map/age-fade";
import { diffFeatureCollection, type FeatureSignatures } from "@/map/source-diff";
import { isMapUsable } from "@/map/map-ready";
import { getDetectionGlyphColor, resolveSingleColor } from "@/map/marker-styling";
import {
  DEFAULT_SELECTED_DASH_PATTERN,
  SELECTED_DASH_PATTERNS,
  type SelectedDashPattern,
} from "@/map/selected-dash-pattern";
import { SNAPSHOT_LAYER_ID, resolveCssVarColor } from "@/map/snapshot-layer-impl";
import {
  type ObjectTrackRecord,
  type TrackMember,
  headTimeMs,
  isPresentedTrack,
  positionedMembers,
} from "@/object-tracks/object-track-store";
import { summaryEnvelope } from "@/object-tracks/track-display";

export const OBJECT_TRACK_SOURCE_ID = "meridian-object-track-source";

// The pattern vocabulary lives in :file:`selected-dash-pattern.ts` (no
// imports) so the Settings drawer can read it without this module's MapLibre
// / store chain; re-exported here for the layer's own callers and tests.
export { DEFAULT_SELECTED_DASH_PATTERN, SELECTED_DASH_PATTERNS, type SelectedDashPattern };
export const OBJECT_TRACK_LAYER_ID = "meridian-object-track-layer";

/** Line width in screen px (the tails' width; the glyph sits on top). */
export const OBJECT_TRACK_LINE_WIDTH_PX = 2;
/** Line width for the selected track (Phase D sets ``selected``). */
export const OBJECT_TRACK_SELECTED_LINE_WIDTH_PX = 4;
/** Opacity of a track whose head is fresh. */
export const OBJECT_TRACK_OPACITY_FRESH = 0.85;
/** Opacity of a track whose head is about to age out of the live window. */
export const OBJECT_TRACK_OPACITY_STALE = 0.3;

/**
 * The line's opacity in each band of the **shared** age-fade schedule
 * (:mod:`map/age-fade` — one table of fractions for the line and the detection
 * glyphs both, so REQ-UI-167's "the same schedule as detection glyphs" is true
 * by construction rather than by two tables agreeing). The values are the
 * line's own: it has no second meaning riding on its opacity, so it paints
 * absolutes where the glyphs apply relative factors.
 */
export const OBJECT_TRACK_AGE_OPACITIES: readonly number[] = [
  OBJECT_TRACK_OPACITY_FRESH,
  0.6,
  OBJECT_TRACK_OPACITY_STALE,
];

/**
 * The band fractions paired with the line's opacities — the shape this
 * module published before the schedule moved to :mod:`map/age-fade`, kept
 * because it reads better in a test and in a review than two parallel arrays.
 */
export const OBJECT_TRACK_AGE_STEPS: ReadonlyArray<readonly [number, number]> =
  AGE_FADE_STEP_FRACTIONS.map(
    (fraction, index) => [fraction, OBJECT_TRACK_AGE_OPACITIES[index]] as const,
  );

/** Options for {@link objectTracksToFeatureCollection}. */
export interface ObjectTracksToFeatureCollectionOptions {
  /** The reference "now" (epoch ms) for the age fraction. */
  readonly now: number;
  /** The deployment live window (ms); the age fraction's denominator. */
  readonly liveWindowMs: number;
  /** The selected ``track_id`` (Phase D), or ``null``. */
  readonly selectedTrackId?: string | null;
}

/** The properties every track feature carries. */
export interface ObjectTrackFeatureProperties {
  readonly track_id: string;
  readonly producer_id: string;
  readonly object_id: string;
  /** Head age / live window, clamped to ``[0, 1]``. */
  readonly age_fraction: number;
  readonly selected: boolean;
}

/**
 * A track's line vertices, cached per members array (feature spec
 * ``ui-track-load-headroom`` §2.4; REQ-UI-194). The store replaces a track's
 * ``members`` array only when that track changes, so an untouched track's
 * vertices are reused by reference frame after frame and the conversion
 * walks only the tracks the frame touched — the builder's cost no longer
 * grows with the members held per track. ``WeakMap`` keys die with the
 * arrays they describe.
 */
const lineCoordinatesByMembers = new WeakMap<readonly TrackMember[], GeoJSON.Position[]>();

/** The unwrapped line through a members array's positioned members (cached). */
export function lineCoordinates(members: readonly TrackMember[]): GeoJSON.Position[] {
  const cached = lineCoordinatesByMembers.get(members);
  if (cached !== undefined) {
    return cached;
  }
  const points: { longitude_deg: number; latitude_deg: number }[] = [];
  for (const m of members) {
    if (m.latitude_deg !== null && m.longitude_deg !== null) {
      points.push({ longitude_deg: m.longitude_deg, latitude_deg: m.latitude_deg });
    }
  }
  const coordinates = points.length < 2 ? [] : toUnwrappedCoordinates(points);
  lineCoordinatesByMembers.set(members, coordinates);
  return coordinates;
}

/**
 * Build the GeoJSON source payload — one ``LineString`` per presented track
 * with two or more positioned members.
 */
export function objectTracksToFeatureCollection(
  tracks: readonly ObjectTrackRecord[],
  options: ObjectTracksToFeatureCollectionOptions,
): GeoJSON.FeatureCollection<GeoJSON.LineString, ObjectTrackFeatureProperties> {
  const features: GeoJSON.Feature<GeoJSON.LineString, ObjectTrackFeatureProperties>[] = [];
  const window = Math.max(options.liveWindowMs, 1);
  for (const track of tracks) {
    if (!isPresentedTrack(track)) {
      continue;
    }
    const coordinates = lineCoordinates(track.members);
    if (coordinates.length < 2) {
      continue; // The head glyph alone stands for it — never a fabricated point.
    }
    const fraction = ageFraction(options.now - headTimeMs(track), window);
    features.push({
      type: "Feature",
      geometry: {
        type: "LineString",
        coordinates,
      },
      properties: {
        track_id: track.track_id,
        producer_id: track.producer_id,
        object_id: track.object_id,
        age_fraction: fraction,
        selected: options.selectedTrackId === track.track_id,
      },
    });
  }
  return { type: "FeatureCollection", features };
}

/** ``line-opacity`` stepped on ``age_fraction`` (REQ-UI-168). */
export function composeAgeOpacityExpression(): ExpressionSpecification {
  return composeAgeFractionExpression(OBJECT_TRACK_AGE_OPACITIES);
}

/** MapLibre line-layer ID for the selected track's patterned overlay (REQ-UI-200). */
export const OBJECT_TRACK_DASH_LAYER_ID = "meridian-object-track-dash-layer";

/**
 * Each pattern's ``line-dasharray``, in multiples of the overlay's own width
 * (:data:`OBJECT_TRACK_DASH_WIDTH_PX`, 4 px) — ``[mark, gap]``:
 *
 * - ``dotted`` — 2 px marks, 4 px gaps: the finest, and the schema default.
 *   With the accent withdrawn the pattern carries more of the emphasis, and a
 *   fine dotted line reads as "this line, marked" where a long dash reads as
 *   a differently drawn track.
 * - ``short_dash`` — 6 px marks, 6 px gaps: the 2026-10-09 hatching, kept for
 *   a dense theatre where two parallel tracks need separating.
 * - ``long_dash`` — 12 px marks, 8 px gaps.
 *
 * Expressed in width units so the marks scale if the overlay's width ever
 * changes; ``line-dasharray`` is zoom-independent in MapLibre, so the
 * on-screen sizes above hold at every zoom.
 */
export const OBJECT_TRACK_DASH_ARRAYS: Readonly<Record<SelectedDashPattern, readonly number[]>> = {
  dotted: [0.5, 1],
  short_dash: [1.5, 1.5],
  long_dash: [3, 2],
};

/** Width of the patterned overlay — the selected line's, so it covers it exactly. */
export const OBJECT_TRACK_DASH_WIDTH_PX = OBJECT_TRACK_SELECTED_LINE_WIDTH_PX;

/** Fallback hatch colour (dark ``--bg-base``) for jsdom / SSR. */
export const OBJECT_TRACK_HATCH_FALLBACK_HEX = "#0f1117";

/**
 * The overlay marks' colour: the base background (``--bg-base``), so the marks
 * read as **cut out of** the line beneath them, whatever colour the operator
 * configured for it.
 *
 * Measured on the §5.11 pass 2026-10-09 and chosen because of it. The marks
 * first painted in a second line colour, which looked right in a screenshot
 * and is wrong: cyan over the dark theme's amber measured **1.13:1** — two
 * hues at nearly one luminance, which is precisely the distinction that
 * disappears for an operator with a colour-vision deficiency, and is weak for
 * anyone at a 4 px line. WCAG 1.4.11 asks 3:1 of a graphical distinction.
 *
 * The base background gives luminance contrast in **both** themes by
 * construction — near-black under a bright line on the dark theme, near-white
 * under a dark line on the light one — which is why it is already the glyph
 * labels' halo colour (`snapshot-layer-impl.getLabelHaloColor`), the same
 * problem of separating a mark from whatever sits under it. That property is
 * what lets the line keep its configured colour (2026-10-19): the cut-outs
 * contrast with any colour the operator can pick.
 */
export function selectedTrackHatchColor(): string {
  return resolveCssVarColor("--bg-base", OBJECT_TRACK_HATCH_FALLBACK_HEX);
}

/** ``line-width`` — wider for the selected track. */
function composeLineWidthExpression(): ExpressionSpecification {
  return [
    "case",
    ["==", ["get", "selected"], true],
    OBJECT_TRACK_SELECTED_LINE_WIDTH_PX,
    OBJECT_TRACK_LINE_WIDTH_PX,
  ];
}

/**
 * Previous-pass feature signatures per map — what the source currently
 * holds, keyed by ``track_id`` — so the next attach can post a diff
 * (feature spec ``ui-track-load-headroom`` §2.4; REQ-UI-194). Cleared on
 * detach; a map that was garbage-collected takes its entry with it.
 */
const trackSignatures = new WeakMap<MapLibreMap, FeatureSignatures>();

/**
 * The diff key for a track feature's properties: ``age_fraction`` by its
 * painted bucket (the ``step`` thresholds in :data:`OBJECT_TRACK_AGE_STEPS`),
 * everything else verbatim — so a track ageing within one opacity step is
 * not an update, and a step crossing is.
 */
export function trackFeatureBucket(properties: ObjectTrackFeatureProperties): string {
  const bucket = ageStepIndex(properties.age_fraction);
  return `${properties.track_id}|${properties.producer_id}|${properties.object_id}|${bucket}|${String(properties.selected)}`;
}

/**
 * Register the source + line layer on the live map, or update the source in
 * place. Idempotent — the first call adds the source (keyed by ``track_id``
 * through ``promoteId``) and the layer; later calls post only the **diff**
 * between what the source holds and ``featureCollection`` through
 * ``GeoJSONSource.updateData`` (a frame touching k tracks costs k updates,
 * never a repost of every line — REQ-UI-194) and re-apply the colour.
 * Inserted **below** the snapshot marker layer so the head glyphs paint over
 * their own line.
 */
export function attachObjectTrackLayer(
  map: MapLibreMap,
  featureCollection: GeoJSON.FeatureCollection<GeoJSON.LineString, ObjectTrackFeatureProperties>,
  singleColor?: string,
  emphasis?: SelectedTrackEmphasis,
): void {
  // No-op safely on a removed / not-ready map (teardown race) — see
  // :file:`map/map-ready.ts`; CLAUDE.md §8.1 fail-safe.
  if (!isMapUsable(map)) {
    return;
  }
  const lineColor = resolveSingleColor(singleColor, getDetectionGlyphColor());
  const existing = map.getSource(OBJECT_TRACK_SOURCE_ID) as GeoJSONSource | undefined;
  if (existing) {
    const { diff, signatures } = diffFeatureCollection(
      trackSignatures.get(map) ?? new Map(),
      featureCollection,
      (feature) => feature.properties.track_id,
      trackFeatureBucket,
    );
    trackSignatures.set(map, signatures);
    if (diff !== null) {
      void existing.updateData(diff);
    }
    if (map.getLayer(OBJECT_TRACK_LAYER_ID)) {
      map.setPaintProperty(OBJECT_TRACK_LAYER_ID, "line-color", lineColor);
    }
    syncSelectedDashLayer(map, emphasis);
    return;
  }
  map.addSource(OBJECT_TRACK_SOURCE_ID, {
    type: "geojson",
    data: featureCollection,
    promoteId: "track_id",
  });
  trackSignatures.set(
    map,
    diffFeatureCollection(
      new Map(),
      featureCollection,
      (feature) => feature.properties.track_id,
      trackFeatureBucket,
    ).signatures,
  );
  const beforeId = map.getLayer(SNAPSHOT_LAYER_ID) ? SNAPSHOT_LAYER_ID : undefined;
  map.addLayer(
    {
      id: OBJECT_TRACK_LAYER_ID,
      type: "line",
      source: OBJECT_TRACK_SOURCE_ID,
      layout: { "line-join": "round", "line-cap": "round" },
      paint: {
        // One colour for every track, the selected one included: the
        // selection rides the width (below) and the overlay, never a hue.
        "line-color": lineColor,
        "line-width": composeLineWidthExpression(),
        "line-opacity": composeAgeOpacityExpression(),
      },
    },
    beforeId,
  );
  syncSelectedDashLayer(map, emphasis);
}

/** How the selected track is emphasised beyond the width. */
export interface SelectedTrackEmphasis {
  /** The Profile's ``map.track.selected_dash`` (REQ-UI-200). */
  readonly selectedDash: boolean;
  /** The Profile's ``map.track.selected_dash_pattern`` (REQ-UI-200 amended 2026-10-19). */
  readonly selectedDashPattern: SelectedDashPattern;
  /** The ``mapSelected`` track, or ``null``. */
  readonly selectedTrackId: string | null;
}

/** The dash layer's filter: the selected track alone, or nothing. */
function selectedTrackFilter(selectedTrackId: string | null): FilterSpecification {
  // A sentinel no track_id can equal, rather than removing the layer, so a
  // selection change is one ``setFilter`` and not an add/remove cycle.
  return ["==", ["get", "track_id"], selectedTrackId ?? "\u0000none"];
}

/**
 * Add, re-filter, re-pattern or remove the selected track's **patterned
 * overlay** (REQ-UI-200).
 *
 * A second line layer on the *same* source, filtered to the selected
 * ``track_id`` and painted with the Profile's ``line-dasharray`` in the base
 * background colour over the solid line — so the selected track alternates
 * between its colour and cut-outs, which separates it from a neighbour
 * running parallel in a way width does not on its own. The marks must
 * contrast with what is under them: painted in the line's colour as well, the
 * overlay differs from the base line only by composited alpha and effectively
 * disappears (caught on the first rendered screenshot of the 2026-10-09 pass,
 * which is why that screenshot is part of the work and not a formality).
 *
 * It is a layer rather than a data-driven property because ``line-dasharray``
 * is **not** data-driven in the MapLibre style spec (it is ``cross-faded``,
 * zoom-parameterised only), so there is no expression that could dash one
 * feature of a layer and not another. One filtered feature makes that
 * irrelevant, and costs a layer that draws a single line. A pattern change is
 * one ``setPaintProperty`` on that layer, not an add/remove cycle.
 *
 * Deliberately **not** in ``ENTITY_SELECTION_LAYERS``: it is a second geometry
 * answering for a track the base line already answers for, at a different
 * width, and the corridor hit test and the deselect decision must query
 * exactly one track layer or they can disagree (REQ-UI-199).
 */
function syncSelectedDashLayer(map: MapLibreMap, emphasis?: SelectedTrackEmphasis): void {
  const wanted = emphasis?.selectedDash === true;
  const present = map.getLayer(OBJECT_TRACK_DASH_LAYER_ID) !== undefined;
  if (!wanted) {
    if (present) {
      map.removeLayer(OBJECT_TRACK_DASH_LAYER_ID);
    }
    return;
  }
  const filter = selectedTrackFilter(emphasis?.selectedTrackId ?? null);
  const dashArray = [
    ...OBJECT_TRACK_DASH_ARRAYS[emphasis?.selectedDashPattern ?? DEFAULT_SELECTED_DASH_PATTERN],
  ];
  if (present) {
    map.setFilter(OBJECT_TRACK_DASH_LAYER_ID, filter);
    map.setPaintProperty(OBJECT_TRACK_DASH_LAYER_ID, "line-color", selectedTrackHatchColor());
    map.setPaintProperty(OBJECT_TRACK_DASH_LAYER_ID, "line-dasharray", dashArray);
    return;
  }
  // Above the base line so the hatching reads over the accent, and still
  // below the snapshot markers so a platform is never hidden by a track.
  const beforeId = map.getLayer(SNAPSHOT_LAYER_ID) ? SNAPSHOT_LAYER_ID : undefined;
  map.addLayer(
    {
      id: OBJECT_TRACK_DASH_LAYER_ID,
      type: "line",
      source: OBJECT_TRACK_SOURCE_ID,
      filter,
      layout: { "line-join": "round", "line-cap": "butt" },
      paint: {
        // The base background, so the marks read as cut out of the line.
        // Two earlier choices were wrong and each took a different kind of
        // evidence to find: painted in the LINE's own colour the overlay
        // differed from the base line only by composited alpha and vanished
        // (the first screenshot); painted in a SECOND line colour it looked
        // right in a screenshot but measured 1.13:1 in dark theme — a
        // hue-only distinction (the §5.11 contrast measurement).
        "line-color": selectedTrackHatchColor(),
        "line-width": OBJECT_TRACK_DASH_WIDTH_PX,
        "line-dasharray": dashArray,
        // The overlay rides the base line's age fade, so a selected track that
        // has gone quiet fades as one object rather than keeping a bright
        // hatch over a faint line.
        "line-opacity": composeAgeOpacityExpression(),
      },
    },
    beforeId,
  );
}

/** Tear down the layer + source (layer before source). */
export function detachObjectTrackLayer(map: MapLibreMap): void {
  // No-op safely on a removed / not-ready map (teardown race).
  if (!isMapUsable(map)) {
    return;
  }
  trackSignatures.delete(map);
  if (map.getLayer(OBJECT_TRACK_DASH_LAYER_ID)) {
    map.removeLayer(OBJECT_TRACK_DASH_LAYER_ID);
  }
  if (map.getLayer(OBJECT_TRACK_LAYER_ID)) {
    map.removeLayer(OBJECT_TRACK_LAYER_ID);
  }
  if (map.getSource(OBJECT_TRACK_SOURCE_ID)) {
    map.removeSource(OBJECT_TRACK_SOURCE_ID);
  }
}

// ---- The click corridor (REQ-UI-199) -----------------------------

/**
 * Half-width, in screen pixels, of the corridor a click may land in and still
 * select a track line.
 *
 * The line renders :data:`OBJECT_TRACK_LINE_WIDTH_PX` wide, and selection used
 * MapLibre's **layer-scoped** click handler, which hit-tests the exact cursor
 * point against the rendered geometry — so the operator was aiming at a 2 px
 * target. Measured on the hermetic rig 2026-10-09: a click selected the track
 * at 0, 1 and 2 px off the line and nothing at 3 px or beyond.
 *
 * ``12`` makes the corridor **24 px across** from the tolerance alone (26 with
 * the line), which is the floor WCAG 2.5.8 sets for a target's narrow
 * dimension — a line is unbounded along its length, so the perpendicular
 * width is the dimension that counts. The design system's stance on that
 * floor is "treat it as a minimum, not a target"
 * (``shared/design-system.md``, the expansion-control note), where a control
 * that technically met 24 × 24 still read as a hairline; this clears it rather
 * than meeting it, and is the first of the three numbers the §5.11 pass is
 * expected to tune. It is wider than the annotation handles'
 * ``HANDLE_QUERY_PX = 8`` because a handle is a visible 10 px box the operator
 * aims at, where a 2 px line is not.
 *
 * A corridor this wide overlaps a neighbour's wherever tracks run close
 * together, which at sixty tracks is most of the time. That is what
 * :func:`nearestTrackAtPoint` is for: overlapping corridors make the pick
 * ambiguous only if you take the topmost feature instead of the closest line.
 *
 * **One constant, two consumers.** The same tolerance is used by
 * :func:`background-deselect.isBackgroundClick`, because a click inside the
 * corridor but off the rendered line must not read as "background" to the
 * map-wide deselect handler — it would clear the selection the corridor just
 * set, on the same click. That race has three editions on record already
 * (see :file:`background-deselect.ts`); this is the constant that stops a
 * fourth.
 */
export const OBJECT_TRACK_HIT_TOLERANCE_PX = 12;

/** A rendered track line the corridor caught: its id and its vertices. */
export interface TrackLineCandidate {
  readonly trackId: string;
  readonly coordinates: readonly GeoJSON.Position[];
}

/** Squared distance from ``p`` to the segment ``a``–``b``, in pixels². */
function squaredDistanceToSegment(
  p: { x: number; y: number },
  a: { x: number; y: number },
  b: { x: number; y: number },
): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSquared = dx * dx + dy * dy;
  // A zero-length segment (a repeated vertex, or a line clipped to one point)
  // is just the point.
  const t =
    lengthSquared === 0
      ? 0
      : Math.min(1, Math.max(0, ((p.x - a.x) * dx + (p.y - a.y) * dy) / lengthSquared));
  const cx = a.x + t * dx;
  const cy = a.y + t * dy;
  return (p.x - cx) ** 2 + (p.y - cy) ** 2;
}

/**
 * Which of the lines the corridor caught the operator meant: the one passing
 * **closest to the click**, measured to its segments rather than to its
 * vertices.
 *
 * A widened corridor catches several lines wherever tracks run near each other
 * — which at sixty tracks is most of the time — so taking the topmost feature
 * would make the pick arbitrary. Distance to the *segment* is what makes the
 * answer the one the operator sees: a click beside the middle of a long leg is
 * on that leg, however far away both its endpoints are, and a click past a
 * short line's end is not on it.
 *
 * Pure, and projection-injected, so it is exercised in screen pixels with no
 * map at all. Cost is bounded by what the corridor returned: the vertices of a
 * handful of candidates, once per click.
 */
export function nearestTrackAtPoint(
  candidates: readonly TrackLineCandidate[],
  point: { x: number; y: number },
  project: (position: GeoJSON.Position) => { x: number; y: number },
): string | null {
  let best: string | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const candidate of candidates) {
    const vertices = candidate.coordinates.map(project);
    if (vertices.length === 0) {
      continue;
    }
    let distance = Number.POSITIVE_INFINITY;
    if (vertices.length === 1) {
      distance = squaredDistanceToSegment(point, vertices[0], vertices[0]);
    }
    for (let i = 1; i < vertices.length; i += 1) {
      distance = Math.min(distance, squaredDistanceToSegment(point, vertices[i - 1], vertices[i]));
    }
    // Strictly closer, so an exact tie keeps the earlier candidate and the
    // pick is deterministic rather than dependent on render order.
    if (distance < bestDistance) {
      bestDistance = distance;
      best = candidate.trackId;
    }
  }
  return best;
}

/** The tolerance box around a click, in the shape ``queryRenderedFeatures`` takes. */
export function trackHitBox(point: { x: number; y: number }): [[number, number], [number, number]] {
  return [
    [point.x - OBJECT_TRACK_HIT_TOLERANCE_PX, point.y - OBJECT_TRACK_HIT_TOLERANCE_PX],
    [point.x + OBJECT_TRACK_HIT_TOLERANCE_PX, point.y + OBJECT_TRACK_HIT_TOLERANCE_PX],
  ];
}

// ---- Focus (REQ-UI-172) ------------------------------------------

/** Camera ease for the track focus (ADR-024 §5's 200 ms, as every layer). */
export const TRACK_FOCUS_DURATION_MS = 200;
/** Padding (px) around the fitted extent so the line is not on the edge. */
export const TRACK_FOCUS_PADDING_PX = 64;
/** The fit never zooms past this — a short track must not become a smear. */
export const TRACK_FOCUS_MAX_ZOOM = 16;

/**
 * The extent to fit for a track (REQ-UI-172): the positioned members' bounds,
 * else the summary's envelope; ``null`` when the track has no positioned
 * member (the panel disables Select on Map for it anyway). A single point
 * yields a degenerate box the caller eases to instead.
 */
export function trackFocusBounds(
  record: ObjectTrackRecord,
): { sw: [number, number]; ne: [number, number] } | null {
  const points = positionedMembers(record);
  if (points.length > 0) {
    let minLon = Number.POSITIVE_INFINITY;
    let minLat = Number.POSITIVE_INFINITY;
    let maxLon = Number.NEGATIVE_INFINITY;
    let maxLat = Number.NEGATIVE_INFINITY;
    for (const p of points) {
      const lon = p.longitude_deg as number;
      const lat = p.latitude_deg as number;
      minLon = Math.min(minLon, lon);
      maxLon = Math.max(maxLon, lon);
      minLat = Math.min(minLat, lat);
      maxLat = Math.max(maxLat, lat);
    }
    return { sw: [minLon, minLat], ne: [maxLon, maxLat] };
  }
  const env = record.summary !== null ? summaryEnvelope(record.summary) : null;
  if (env === null) {
    return null;
  }
  return { sw: [env.minLon, env.minLat], ne: [env.maxLon, env.maxLat] };
}

/**
 * How often the age fade re-evaluates with no store change (a silent track
 * still fades), for the shipped default window. Both map layers now derive
 * their interval from the deployment's live window via
 * :func:`age-fade.ageFadeTickMs`, which returns exactly this at the 10-minute
 * default and a finer interval for a shorter window; the constant is kept as
 * the published name for that ceiling.
 */
export const OBJECT_TRACK_AGE_TICK_MS = AGE_FADE_TICK_CEILING_MS;
